import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/rn-logo.default.js';

/** @type {readonly ['full', 'symbol', 'wordmark']} */
export const LOGO_VARIANTS = Object.freeze(['full', 'symbol', 'wordmark']);

/** @type {readonly ['sm', 'md', 'lg', 'xl']} */
export const LOGO_SIZES = Object.freeze(['sm', 'md', 'lg', 'xl']);

/** @type {readonly ['brand', 'neutral', 'inverse']} */
export const LOGO_TONES = Object.freeze(['brand', 'neutral', 'inverse']);

class RunRnLogo extends skinResolutionMixin(RunElement) {
  // ─── Private state ────────────────────────────────────────────────────────
  #variant = 'full';
  #size = 'md';
  #tone = 'brand';
  #decorative = false;
  #label = 'Logo';

  // ─── Skin registry ────────────────────────────────────────────────────────
  // Default skin is always bundled — zero-async first paint, no flash.
  static defaultSkin = defaultSkin;

  // Every non-default skin is lazy. The default skin is NOT listed here.
  static skinLoaders = {};

  // ─── Observed attributes ──────────────────────────────────────────────────
  static get observedAttributes() {
    return ['variant', 'size', 'tone', 'decorative', 'label'];
  }

  // ─── Connected ────────────────────────────────────────────────────────────
  connectedCallback() {
    super.connectedCallback();
    this.#updateAria();
    this.requestUpdate();
  }

  // ─── Attribute routing ────────────────────────────────────────────────────
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'variant':    this.variant    = newValue; break;
      case 'size':       this.size       = newValue; break;
      case 'tone':       this.tone       = newValue; break;
      case 'decorative': this.decorative = newValue !== null; break;
      case 'label':      this.label      = newValue; break;
    }
  }

  // ─── Public property: variant ─────────────────────────────────────────────
  get variant() {
    return this.#variant;
  }

  set variant(value) {
    const coerced = LOGO_VARIANTS.includes(String(value).trim())
      ? String(value).trim()
      : 'full';
    if (this.#variant === coerced) return;
    this.#variant = coerced;
    this.setAttribute('variant', coerced);
    this.requestUpdate();
  }

  // ─── Public property: size ────────────────────────────────────────────────
  get size() {
    return this.#size;
  }

  set size(value) {
    const coerced = LOGO_SIZES.includes(String(value).trim())
      ? String(value).trim()
      : 'md';
    if (this.#size === coerced) return;
    this.#size = coerced;
    this.setAttribute('size', coerced);
    this.requestUpdate();
  }

  // ─── Public property: tone ────────────────────────────────────────────────
  get tone() {
    return this.#tone;
  }

  set tone(value) {
    const coerced = LOGO_TONES.includes(String(value).trim())
      ? String(value).trim()
      : 'brand';
    if (this.#tone === coerced) return;
    this.#tone = coerced;
    this.setAttribute('tone', coerced);
    this.requestUpdate();
  }

  // ─── Public property: decorative ──────────────────────────────────────────
  get decorative() {
    return this.#decorative;
  }

  set decorative(value) {
    const coerced = Boolean(value);
    if (this.#decorative === coerced) return;
    this.#decorative = coerced;
    if (coerced) {
      this.setAttribute('decorative', '');
    } else {
      this.removeAttribute('decorative');
    }
    this.#updateAria();
    this.requestUpdate();
  }

  // ─── Public property: label ───────────────────────────────────────────────
  get label() {
    return this.#label;
  }

  set label(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#label === coerced) return;
    this.#label = coerced;
    if (coerced) {
      this.setAttribute('label', coerced);
    } else {
      this.removeAttribute('label');
    }
    this.#updateAria();
    this.requestUpdate();
  }

  // ─── Post-skin host work ──────────────────────────────────────────────────
  // updated() is present because host ARIA must be re-applied after the
  // skin re-renders (skin writes innerHTML, which does not disturb host
  // attributes, but ARIA on the host must stay current).
  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#updateAria();
  }

  // ─── Private: ARIA ────────────────────────────────────────────────────────
  // Decorative mode removes role + aria-label rather than merely adding
  // aria-hidden: role="img" left in place alongside aria-hidden fails JAWS.
  // An empty label is treated the same way — role="img" without an
  // accessible name is an a11y defect, so the component hides itself.
  #updateAria() {
    if (this.#decorative || !this.#label) {
      this.removeAttribute('role');
      this.removeAttribute('aria-label');
      this.setAttribute('aria-hidden', 'true');
    } else {
      this.setAttribute('role', 'img');
      this.setAttribute('aria-label', this.#label);
      this.removeAttribute('aria-hidden');
    }
  }
}

// ─── Registration ─────────────────────────────────────────────────────────────
if (!customElements.get('run-rn-logo')) {
  customElements.define('run-rn-logo', RunRnLogo);
}

export { RunRnLogo };
export default RunRnLogo;
